import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Eye, EyeOff, X, AlertCircle } from "lucide-react";
import { NovaLogo } from "@/components/NovaLogo";

// Improved welcome / login screen — addresses launch-list #9:
// - Eye icon to show/hide password
// - X clear button on email
// - Forgot password link
// - Better error messages (mapped friendly copy)
// - Loading spinner during sign-in
// - Keep users signed in until they log out (handled by 7-day JWT cookie/localStorage)

export default function WelcomeScreen() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const friendlyError = (raw) => {
    const msg = (raw || "").toString().toLowerCase();
    if (msg.includes("invalid") || msg.includes("incorrect") || msg.includes("credentials")) {
      return "That email and password don't match. Double-check or try resetting your password.";
    }
    if (msg.includes("not found") || msg.includes("no user")) {
      return "We couldn't find an account with that email. Want to create one?";
    }
    if (msg.includes("already") || msg.includes("exists")) {
      return "An account with that email already exists. Try signing in instead.";
    }
    if (msg.includes("network") || msg.includes("fetch") || msg.includes("failed")) {
      return "Network hiccup. Check your connection and try again.";
    }
    if (msg.includes("password")) {
      return "Password needs to be at least 6 characters.";
    }
    return raw || "Something went wrong. Try again in a moment.";
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email.trim(), password);
        // Existing user — only force onboarding if profile is incomplete.
        let target = "/home";
        try {
          const { data } = await (await import("@/lib/api")).default.get("/profile/onboarding-status");
          if (data && data.onboarded === false) target = "/onboarding";
        } catch (_) { /* fall through to /home */ }
        navigate(target, { replace: true });
      } else {
        await register(email.trim(), password, name.trim() || null);
        // Brand new account → always start in onboarding so we can collect
        // the basic personalisation fields the AI needs from day one.
        navigate("/onboarding", { replace: true });
      }
    } catch (err) {
      setError(friendlyError(formatApiError(err)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="screen fade-in" data-testid="welcome-screen">
      <div className="top-bar">
        <div className="logo-mark" data-testid="welcome-logo" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <NovaLogo size={22} />
          <span>NOVA</span>
        </div>
        <span style={{ width: 32 }} />
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", paddingTop: 8 }}>
        <div style={{ marginBottom: 24, display: "flex", justifyContent: "flex-start" }}>
          <NovaLogo size={84} />
        </div>
        <div className="eyebrow" style={{ marginBottom: 16, letterSpacing: "0.30em" }}>
          NOVA · BECOME YOUR BEST. EVERY DAY.
        </div>
        <h1 className="h1" style={{ marginBottom: 12 }}>
          Become<br />
          <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>your best</em>,<br />
          every day.
        </h1>
        <p className="body" style={{ marginTop: 18, maxWidth: 320 }}>
          AI-personalised fitness, nutrition, skin and style — built into one premium app.
        </p>
      </div>

      <form onSubmit={submit} className="stagger" style={{ display: "flex", flexDirection: "column", gap: 14, paddingBottom: 60 }}>
        {mode === "signup" && (
          <div className="field">
            <label className="label" htmlFor="name">Name</label>
            <input
              id="name"
              type="text"
              className="input"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="welcome-name-input"
            />
          </div>
        )}
        <div className="field">
          <label className="label" htmlFor="email">Email</label>
          <div style={{ position: "relative" }}>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              className="input"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="welcome-email-input"
              style={{ paddingRight: email ? 38 : 14 }}
            />
            {email && (
              <button
                type="button"
                onClick={() => setEmail("")}
                aria-label="Clear email"
                data-testid="welcome-email-clear"
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.08)",
                  color: "var(--text-faint)",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <X size={13} strokeWidth={2} />
              </button>
            )}
          </div>
        </div>
        <div className="field">
          <label className="label" htmlFor="password">Password</label>
          <div style={{ position: "relative" }}>
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              required
              minLength={6}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              className="input"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="welcome-password-input"
              style={{ paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              data-testid="welcome-password-toggle"
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "transparent",
                border: "none",
                color: "var(--text-faint)",
                cursor: "pointer",
                padding: 4,
              }}
            >
              {showPassword ? <EyeOff size={16} strokeWidth={1.8} /> : <Eye size={16} strokeWidth={1.8} />}
            </button>
          </div>
        </div>

        {mode === "login" && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: -6 }}>
            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="tiny linkish"
              data-testid="welcome-forgot-password"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "var(--text-dim)",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              Forgot password?
            </button>
          </div>
        )}

        {error && (
          <div
            className="error-text"
            data-testid="welcome-error"
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              padding: "10px 12px",
              background: "rgba(220,80,80,0.08)",
              border: "1px solid rgba(220,80,80,0.25)",
              borderRadius: 10,
              color: "rgba(255,180,180,0.95)",
            }}
          >
            <AlertCircle size={14} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
            <span style={{ fontSize: 13, lineHeight: 1.4 }}>{error}</span>
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading}
          data-testid="welcome-submit-btn"
          style={{ marginTop: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
        >
          {loading ? (
            <>
              <span className="spinner" />
              <span>{mode === "login" ? "Signing in…" : "Creating your account…"}</span>
            </>
          ) : (
            mode === "login" ? "Continue" : "Create account"
          )}
        </button>

        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: 4 }}>
          <button
            type="button"
            className="linkish tiny"
            onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); }}
            data-testid="welcome-toggle-mode-btn"
            style={{ textDecoration: "none", letterSpacing: "0.18em", textTransform: "uppercase" }}
          >
            {mode === "login" ? "Create account" : "I have an account"}
          </button>
        </div>

        {/* Legal footer — required for App Store / public deployment */}
        <div
          className="tiny"
          data-testid="welcome-legal-footer"
          style={{ marginTop: 22, textAlign: "center", color: "var(--text-faint)", letterSpacing: "0.04em", lineHeight: 1.6 }}
        >
          By continuing you agree to our{" "}
          <a href="/legal/terms" data-testid="welcome-terms-link" style={{ color: "var(--text-dim)" }}>Terms of Service</a>
          {" "}and{" "}
          <a href="/legal/privacy" data-testid="welcome-privacy-link" style={{ color: "var(--text-dim)" }}>Privacy Policy</a>.
        </div>
      </form>
    </div>
  );
}
